import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { twMerge } from "tailwind-merge";

import { InputIME } from "@/components/input-ime";
import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useAddWatchlist } from "@/features/watchlist/services/add-watchlist";
import { useLang } from "@/hooks/use-lang";

import { TickIcon } from "ui/icon/shared";

import { WatchListMenu } from "./WatchListMenu";

export const HeaderPopper = ({
  setIsPopperOpen,
}: {
  setIsPopperOpen: (value: boolean) => void;
}) => {
  const { t } = useLang();
  const { profile, isAuthenticated } = useAuth();
  const isAddable = isAuthenticated && profile?.plan !== "Free";
  const { mutate: addWatchlist } = useAddWatchlist();

  const [isEditing, setIsEditing] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState("");

  const onAddWatchlistButtonClick = () => {
    if (!newWatchlistName) {
      setIsEditing(!isEditing);
      return;
    }
    handleAddWatchlist();
  };

  const handleNewWatchlistInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      handleAddWatchlist();
    }
    if (e.key === "Escape") {
      setIsEditing(false);
      setNewWatchlistName("");
    }
  };

  const handleAddWatchlist = () => {
    const watchlistName = newWatchlistName.trim();
    if (!watchlistName) return;

    setIsEditing(false);
    setNewWatchlistName("");

    addWatchlist(
      { watchlistName },
      {
        onSuccess: () => {
          toast.success(
            t.layout.watchlist.addWatchlistSuccessToast(watchlistName)
          );
        },
        onError: () => {
          toast.error(t.layout.watchlist.addWatchlistFailToast(watchlistName));
        },
      }
    );
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      className="absolute left-0 top-11 z-30 mx-auto w-full rounded-md border border-zinc-200 bg-white py-1"
    >
      <AddWatchlistButton
        isDisabled={!isAddable}
        isEditing={isEditing}
        newWatchlistName={newWatchlistName}
        onAddWatchlistButtonClick={onAddWatchlistButtonClick}
      />
      <div className="border-b border-zinc-200" />
      <WatchListMenu setIsPopperOpen={setIsPopperOpen} />
      {isEditing && (
        <div className="mb-2 px-2">
          <InputIME
            aria-label="new watchlist name"
            value={newWatchlistName}
            onChange={(e) => setNewWatchlistName(e.target.value)}
            className="w-full max-w-[150px]"
            autoFocus
            maxLength={20}
            onKeyDown={handleNewWatchlistInputKeyDown}
          />
        </div>
      )}
      {!isAddable && (
        <>
          <div className="border-b border-zinc-200" />
          <SubscribeTips />
        </>
      )}
    </div>
  );
};

const AddWatchlistButton = ({
  newWatchlistName,
  isEditing,
  onAddWatchlistButtonClick,
  isDisabled,
}: {
  newWatchlistName: string;
  isEditing: boolean;
  onAddWatchlistButtonClick: () => void;
  isDisabled: boolean;
}) => {
  const { t } = useLang();
  return (
    <button
      aria-label="add new watchlist"
      aria-disabled={isDisabled}
      className={twMerge(
        "flex w-full cursor-pointer select-none items-center justify-start gap-1.5 px-4 py-3",
        isDisabled
          ? "cursor-default text-zinc-500"
          : "text-sky-700 hover:bg-sky-50"
      )}
      onClick={isDisabled ? undefined : onAddWatchlistButtonClick}
    >
      {isEditing && newWatchlistName ? (
        <TickIcon
          className={twMerge(isDisabled ? "stroke-zinc-500" : "stroke-sky-700")}
        />
      ) : (
        <PlusCircleIcon className="h-6 w-6" />
      )}
      <p className="text-sm font-normal">
        {isEditing && newWatchlistName
          ? t.layout.watchlist.create
          : t.layout.watchlist.newWatchlist}
      </p>
    </button>
  );
};

const SubscribeTips = () => {
  const { t, lang } = useLang();
  return (
    <div className="flex cursor-default flex-col gap-1 px-4 py-2">
      <p
        className="text-sm font-bold text-yellow-600"
        aria-label="subscription tips"
      >
        {t.layout.watchlist.limit}
      </p>
      <Link
        href={`/${lang}${links.stockMiningPricing}`}
        className="cursor-pointer text-xs font-medium text-sky-700 underline underline-offset-2"
        aria-label="view other plans"
      >
        {t.layout.watchlist.seePlans}
      </Link>
    </div>
  );
};
